import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import CourseModule from './CourseModule'
import VideoPlayer from './VideoPlayer'
import CourseDocument from './CourseDocument'
import CourseQuiz from './CourseQuiz' 
import CourseSurvey from './CourseSurvey'
import ScormPlayer from './ScormPlayer'
import NoData from '../../../../components/common/NoData'
import { decodeData } from '../../../../utils/encodeAndEncode';
import { getModuleList, getMetadataCourseDetails, GenerateCertificate } from '../../../../services/userService';
import { toast } from 'react-toastify';

const DefaultProfile = '/images/profile/default-profile.png';

function processCourseData(data) {
  if (!data) return data;
  const processed = { ...data };
  if (typeof processed.tags === 'string') {
    try { processed.tags = JSON.parse(processed.tags); } catch { processed.tags = []; }
  }
  if (typeof processed.course_info === 'string') {
    try { processed.course_info = JSON.parse(processed.course_info); } catch { processed.course_info = []; }
  }
  return processed;
}

function applySequentialLocks(modules) {
  if (!Array.isArray(modules)) return [];

  let previousModulesComplete = true;
  let isVeryFirstItem = true;

  return modules.map((module) => {
    const content = Array.isArray(module.content) ? module.content : [];
    const moduleIsLocked = !previousModulesComplete;

    let previousContentComplete = true;
    const lockedContent = content.map((item, index) => {
      const isFirstItem = index === 0;
      const itemCompleted = item.completed === 1;
      const itemIsLocked = isVeryFirstItem ? false : (moduleIsLocked || (!isFirstItem && !previousContentComplete));
      if (isVeryFirstItem) isVeryFirstItem = false;
      if (!itemCompleted) previousContentComplete = false;
      return { ...item, isLocked: itemIsLocked };
    });

    const moduleComplete = lockedContent.length > 0 && lockedContent.every((i) => i.completed === 1);
    previousModulesComplete = previousModulesComplete && moduleComplete;

    return {
      ...module,
      isLocked: moduleIsLocked,
      content: lockedContent,
    };
  });
}

function CourseWatch() {
  const [moduleList, setModuleList] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [courseProgress, setCourseProgress] = useState(0);
  const [mentorId, setMentorId] = useState(null);
  const [courseInfo, setCourseInfo] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [totalLessons, setTotalLessons] = useState(0);
  const [isCourseCompleted, setIsCourseCompleted] = useState(false);
  const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);

  const { encodedId } = useParams();
  const navigate = useNavigate();
  const decoded = decodeData(encodedId);
  const courseId = decoded?.id;

  useEffect(() => {
    if (courseId) {
      fetchModuleList();
      fetchCourseDetails();
    } else {
      setError('Invalid course ID');
      setIsLoading(false);
    }
  }, [courseId]);

  async function fetchCourseDetails() {
    try {
      const response = await getMetadataCourseDetails({ course_id: courseId });
      if (response?.data) {
        setCourseInfo(processCourseData(response.data));
      }
    } catch (err) {
      console.error('Error fetching course details:', err);
    }
  }

  // Save last-viewed content to localStorage whenever selectedContent changes
  useEffect(() => {
    if (selectedContent?.id && selectedContent?.type && courseId) {
      try {
        localStorage.setItem(
          `course_last_content_${courseId}`,
          JSON.stringify({ id: selectedContent.id, type: selectedContent.type })
        );
      } catch {}
    }
  }, [selectedContent?.id, selectedContent?.type, courseId]);

  // Effect to select content when moduleList is loaded
  useEffect(() => {
    if (moduleList.length > 0 && !selectedContent) {
      const allContent = moduleList.flatMap((m) => m.content || []);

      // 1. Try to restore last-viewed content from localStorage
      let contentToSelect = null;
      try {
        const raw = localStorage.getItem(`course_last_content_${courseId}`);
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved?.id && saved?.type) {
            const savedContent = allContent.find(
              (item) => item.id === saved.id && item.type === saved.type
            );
            if (savedContent) contentToSelect = savedContent;
          }
        }
      } catch {}

      // 2. Fall back to first incomplete unlocked content
      if (!contentToSelect) {
        contentToSelect = allContent.find((item) => !item.isLocked && !Boolean(item.completed));
      }

      // 3. Fall back to first unlocked content (all completed case)
      if (!contentToSelect) {
        contentToSelect = allContent.find((item) => !item.isLocked);
      }

      if (contentToSelect) setSelectedContent(contentToSelect);
    }
    // Calculate total lessons
    const total = moduleList.reduce((sum, module) => sum + (module.content?.length || 0), 0);
    setTotalLessons(total);

    // Check if course is 100% completed
    if (moduleList.length > 0) {
      const allContent = moduleList.flatMap((m) => m.content || []);
      const completed = allContent.every((item) => item.completed === 1);
      setIsCourseCompleted(completed && allContent.length > 0);
    }
  }, [moduleList]);

  // Update current lesson index when content changes
  useEffect(() => {
    if (selectedContent && moduleList.length > 0) {
      let index = 0;
      for (const module of moduleList) {
        const contentIndex = module.content?.findIndex(item => item.id === selectedContent.id && item.type === selectedContent.type);
        if (contentIndex !== -1) {
          setCurrentLessonIndex(index + contentIndex + 1);
          break;
        }
        index += module.content?.length || 0;
      }
    }
  }, [selectedContent, moduleList]);

  // Handle body overflow when mobile menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [showMobileMenu]);

  async function fetchModuleList() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getModuleList({ course_id: courseId });

      if (response?.data?.response) {
        const mentorIdFromResponse = response.data.mentor_id;
        if (mentorIdFromResponse) setMentorId(mentorIdFromResponse);

        const sortedModules = response.data.response.map(module => ({
          ...module,
          content: Array.isArray(module.content)
            ? [...module.content]
                .map(item => ({
                  ...item,
                  completed: Number(item.completed) === 1 ? 1 : 0
                }))
                .sort((a, b) => (a.serial_no || 0) - (b.serial_no || 0))
            : []
        }));

        const lockedModules = applySequentialLocks(sortedModules);
        setModuleList(lockedModules);
      } else {
        setError('No course content available');
      }
    } catch (error) {
      console.error('Error fetching module list:', error);
      
      // Check if course is expired (403 status)
      if (error.response?.status === 403) {
        setError('Your access to this course has expired. Please renew to continue.');
      } else {
        setError('No course content available');
      }
      setModuleList([]);
    } finally {
      setIsLoading(false);
    }
  }

  const handleContentClick = (content) => {
    if (!content) return;

    // Check if content is locked
    if (content.isLocked) {
      // Show toast notification
      const message = 'Please complete the previous content to unlock this item';
      toast.warn(message, { position: 'top-center', autoClose: 2500 });
      return;
    }
    
    setSelectedContent(content);
    setShowMobileMenu(false);
  };

  const handleGenerateCertificate = async () => {
    if (!courseInfo?.certificate_template_id) return;
    setIsGeneratingCertificate(true);
    try {
      const response = await GenerateCertificate({ course_id: courseId });
      if (response.success) {
        toast.success('Certificate generated successfully!', { position: 'top-center', autoClose: 3000 });
        
        setTimeout(() => {
          navigate('/user/certificates');
        }, 2000);
      } else {
        toast.error('Failed to generate certificate', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error('Error generating certificate. Please try again.', { position: 'top-center' });
    } finally {
      setIsGeneratingCertificate(false);
    }
  };

  const handleNextLesson = () => {
    if (!selectedContent || moduleList.length === 0) return;
    
    // First, ensure current content is marked as complete
    const updatedModules = moduleList.map(module => ({
      ...module,
      content: module.content.map(item =>
        (item.id === selectedContent.id && item.type === selectedContent.type) ? { ...item, completed: 1 } : item
      )
    }));
    
    const relockedModules = applySequentialLocks(updatedModules);
    
    let found = false;
    let nextContent = null;

    for (const module of relockedModules) {
      for (const item of module.content || []) {
        if (!found) {
          if (item.id === selectedContent.id && item.type === selectedContent.type) {
            found = true;
          }
          continue;
        }

        if (!item.isLocked) {
          nextContent = item;
          break;
        }

        // With sequential locking, if the immediate next is locked, the rest will be too.
        toast.warn('Please complete the previous content to unlock this item', { position: 'top-center', autoClose: 2500 });
        return;
      }
      if (nextContent) break;
    }

    if (!nextContent) {
      navigate('/user/courses?tab=browse');
      return;
    }

    setModuleList(relockedModules);
    setSelectedContent(nextContent);
  };

  const handleVideoProgress = async (videoId) => {
    setModuleList(prevModules => {
      const updated = prevModules.map(module => ({
        ...module,
        content: module.content.map(item =>
          (item.id === videoId && item.type === 'video') ? { ...item, completed: 1 } : item
        )
      }));
      return applySequentialLocks(updated);
    });
  };

  const handleAssessmentCompletion = async (assessmentId, isPassed) => {
    setModuleList(prevModules => {
      const updated = prevModules.map(module => ({
        ...module,
        content: module.content.map(item =>
          (item.id === assessmentId && item.type === 'assessment') ? { ...item, completed: 1 } : item
        )
      }));
      return applySequentialLocks(updated);
    });
  };

  const handleSurveyCompletion = async (surveyId) => {
    setModuleList(prevModules => {
      const updated = prevModules.map(module => ({
        ...module,
        content: module.content.map(item =>
          (item.id === surveyId && item.type === 'survey') ? { ...item, completed: 1 } : item
        )
      }));
      return applySequentialLocks(updated);
    });
  };

  const handleDocumentCompletion = async (documentId) => {
    setModuleList(prevModules => {
      const updated = prevModules.map(module => ({
        ...module,
        content: module.content.map(item =>
          (item.id === documentId && item.type === 'document') ? { ...item, completed: 1 } : item
        )
      }));
      return applySequentialLocks(updated);
    });
  };

  const handleScormCompletion = useCallback((scormContentId) => {
    setModuleList(prevModules => {
      const updated = prevModules.map(module => ({
        ...module,
        content: module.content.map(item =>
          (item.id === scormContentId && item.type === 'scorm') ? { ...item, completed: 1 } : item
        )
      }));
      return applySequentialLocks(updated);
    });
  }, []);

  // Handle course progress updates from CourseModule
  const handleCourseProgressUpdate = (progress) => {
    // setCourseProgress(progress);
    // // console.log('📊 Course progress updated to:', progress + '%');
  };

  const PRIMARY = '#7083FF';

  const LoadingSpinner = () => (
    <div className="min-h-[60vh] flex flex-col justify-center items-center px-4 py-12">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-[#7083FF]" role="status" aria-label="Loading" />
      <p className="mt-4 text-sm font-medium text-gray-500">Loading course content...</p>
    </div>
  );

  const ErrorMessage = ({ message }) => (
    <div className="min-h-[60vh] flex flex-col justify-center items-center px-6 py-12">
      <NoData message={message} />
      <button
        type="button"
        onClick={() => navigate('/user/courses')}
        className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#7083FF]/30 focus:ring-offset-2 transition"
        style={{ backgroundColor: PRIMARY }}
      >
        <Icon icon="mdi:arrow-left" width="20" height="20" />
        Back to Courses
      </button>
    </div>
  );

  const getContentTypeLabel = (type) => {
    const t = type?.toLowerCase();
    if (t === 'video') return 'Video';
    if (t === 'document') return 'Document';
    if (t === 'assessment') return 'Assessment';
    if (t === 'survey') return 'Survey';
    if (t === 'scorm') return 'SCORM';
    return 'Content';
  };

  const renderContent = () => {
    if (!selectedContent) {
      return (
        <div className="flex justify-center items-center min-h-[280px] py-12">
          <NoData caption="No content available in this course." />
        </div>
      );
    }

    const selectedModule = moduleList.find((m) =>
      (m.content || []).some((item) => item.id === selectedContent.id && item.type === selectedContent.type)
    );
    const selectedModuleName = selectedModule?.module_name;
    const selectedLessonName = selectedContent?.title;

    switch (selectedContent.type?.toLowerCase()) {
      case 'video':
        return (
          <>
            <VideoPlayer
              videoUrl={selectedContent.url}
              contentData={selectedContent}
              courseId={courseId}
              mentorId={mentorId}
              onVideoProgress={handleVideoProgress}
            />
          </>
        );
      case 'document':
        return <CourseDocument
          documentUrl={selectedContent.url}
          documentData={selectedContent}
          onDocumentComplete={handleDocumentCompletion}
          currentLessonIndex={currentLessonIndex}
          totalLessons={totalLessons}
          onNextLesson={handleNextLesson}
        />;
      case 'assessment':
        return <CourseQuiz
          assessmentId={selectedContent.id}
          moduleData={selectedContent}
          moduleName={selectedModuleName}
          lessonName={selectedLessonName}
          onAssessmentComplete={handleAssessmentCompletion}
          currentLessonIndex={currentLessonIndex}
          totalLessons={totalLessons}
          onNextLesson={handleNextLesson}
        />;
      case 'survey':
        return <CourseSurvey
          surveyId={selectedContent.id}
          moduleData={selectedContent}
          onSurveyComplete={handleSurveyCompletion}
          currentLessonIndex={currentLessonIndex}
          totalLessons={totalLessons}
          onNextLesson={handleNextLesson}
        />;
      case 'scorm':
        return <ScormPlayer
          scormContentId={selectedContent.id}
          title={selectedContent.title}
          onComplete={handleScormCompletion}
          currentLessonIndex={currentLessonIndex}
          totalLessons={totalLessons}
          onNextLesson={handleNextLesson}
        />;
      default:
        return (
          <div className="text-center p-5">
            <p>Unsupported content type: {selectedContent.type}</p>
          </div>
        );
    }
  };

  const courseName = courseInfo?.course_name || moduleList[0]?.module_name || 'Course';
  const courseDesc = courseInfo?.course_desc || '';
  const durationEst = courseInfo?.courseMeta?.totalVideoDuration || '—';
  const language = courseInfo?.course_language || 'English';
  const trainer = courseInfo?.trainer || null;
  const hasCertificate = Boolean(courseInfo?.certificate_template_id);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="min-h-screen">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-1">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <button
            type="button"
            onClick={() => navigate('/user/courses')}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#7083FF] transition-colors py-2 -ml-2 px-2 rounded-lg hover:bg-white"
          >
            <Icon icon="mdi:arrow-left" width="20" height="20" />
            Back to Courses
          </button>
          <button
            type="button"
            onClick={() => setShowMobileMenu(true)}
            className="lg:hidden flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 shadow-sm hover:border-[#7083FF]/40 hover:text-[#7083FF] transition-colors"
            aria-label="Open course menu"
          >
            <Icon icon="mdi:format-list-bulleted" width="20" height="20" />
            <span className="text-sm font-medium">Chapters</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-6">
          {/* Sidebar: Course modules */}
          {showMobileMenu && (
            <div
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
              aria-hidden
              onClick={() => setShowMobileMenu(false)}
            />
          )}
          <div
            className={`w-full lg:w-[450px] lg:flex-shrink-0 ${
              showMobileMenu ? 'fixed right-0 top-0 bottom-0 z-50 w-full max-w-sm bg-gray-50 flex flex-col shadow-xl lg:relative lg:shadow-none lg:max-w-none' : ''
            }`}
          >
            {showMobileMenu && (
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-white shrink-0 lg:hidden">
                <h2 className="text-lg font-semibold text-gray-900">Course Content</h2>
                <button
                  type="button"
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Close menu"
                >
                  <Icon icon="mdi:close" width="24" height="24" />
                </button>
              </div>
            )}
            <div className={`${showMobileMenu ? 'flex-1 overflow-auto p-4 lg:p-0' : ''} lg:sticky lg:top-6 space-y-7`}>
              <div className="rounded-2xl shadow-sm border border-gray-200 bg-white overflow-hidden">
                <CourseModule
                  courseModules={moduleList}
                  courseId={courseId}
                  onContentClick={handleContentClick}
                  selectedContent={selectedContent}
                  courseName={courseName}
                />
              </div>

              {trainer?.name && (
                <div className="rounded-2xl shadow-sm border border-gray-200 bg-white px-6 py-5">
                  <div className="flex items-center gap-4">
                    <img
                      src={trainer?.profile || DefaultProfile}
                      alt={trainer?.name || 'Author'}
                      className="w-12 h-12 rounded-full object-cover border border-gray-100 shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500">Author</p>
                      <p className="font-semibold text-gray-900 truncate">{trainer?.name}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main content column */}
          <div className="w-full lg:flex-1 min-w-0 space-y-6">
            {/* Certificate Generation Banner */}
            {hasCertificate && isCourseCompleted && (
              <div className="rounded-2xl shadow-sm border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Icon icon="mdi:trophy" width={24} height={24} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Congratulations! 🎉</h3>
                      <p className="text-sm text-gray-600">You've completed this course. Generate your certificate now.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerateCertificate}
                    disabled={isGeneratingCertificate}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg shrink-0"
                  >
                    {isGeneratingCertificate ? (
                      <>
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:certificate" width={20} height={20} />
                        Generate Certificate
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Current content card with video, title, tabs */}
            <div className="rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {selectedContent?.type?.toLowerCase() === 'video' ? (
                <VideoPlayer
                  videoUrl={selectedContent.url}
                  contentData={selectedContent}
                  courseId={courseId}
                  mentorId={mentorId}
                  onVideoProgress={handleVideoProgress}
                  currentLessonIndex={currentLessonIndex}
                  totalLessons={totalLessons}
                  onNextLesson={handleNextLesson}
                />
              ) : selectedContent?.type?.toLowerCase() === 'document' ? (
                renderContent()
              ) : selectedContent?.type?.toLowerCase() === 'survey' ? (
                <div className="p-6 sm:p-8">
                  <CourseSurvey
                    surveyId={selectedContent.id}
                    moduleData={selectedContent}
                    onSurveyComplete={handleSurveyCompletion}
                    currentLessonIndex={currentLessonIndex}
                    totalLessons={totalLessons}
                    onNextLesson={handleNextLesson}
                  />
                </div>
              ) : selectedContent?.type?.toLowerCase() === 'scorm' ? (
                renderContent()
              ) : (
                <div className="p-6 sm:p-8">{renderContent()}</div>
              )}
            </div>

            {/* Description */}
            <div className="rounded-2xl shadow-sm border border-gray-200 bg-white p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-5">Description</h3>
              <div className="text-gray-600 text-lg leading-relaxed">
                {courseDesc ? (
                  <>
                    <p className={descriptionExpanded ? '' : 'line-clamp-3'}>{courseDesc}</p>
                    {courseDesc.length > 180 && (
                      <button
                        type="button"
                        onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                        className="text-[#7083FF] font-medium mt-1.5 hover:underline text-sm"
                      >
                        {descriptionExpanded ? 'Read less' : 'Read more'}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-gray-400">No description available.</p>
                )}
              </div>
            </div>

            {/* Details content */}
            <div className="rounded-2xl shadow-sm border border-gray-200 bg-white p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Details content</h3>
              <ul className="space-y-5">
                <li className="flex items-center gap-5 text-lg text-gray-600">
                  <Icon icon="mdi:clock-outline" width="24" height="24" className="text-gray-400" />
                  <span>{durationEst} Estimation</span>
                </li>
                <li className="flex items-center gap-5 text-lg text-gray-600">
                  <Icon icon="mdi:earth" width="24" height="24" className="text-gray-400" />
                  <span>{language}</span>
                </li>
                {hasCertificate && (
                  <li className="flex items-center gap-5 text-lg text-gray-600">
                    <Icon icon="mdi:certificate-outline" width="24" height="24" className="text-gray-400" />
                    <span>Certification on completion</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseWatch;